/**
 * Maps English amenity/utility names to Arabic translations.
 * Used for translating property features that are stored as English strings in the database.
 */

const amenityMap: Record<string, string> = {
  // From AMENITIES_LIST
  'Parking': 'موقف سيارات',
  'Elevator': 'مصعد',
  'Swimming Pool': 'مسبح',
  'Garden': 'حديقة',
  'Balcony': 'شرفة',
  'Private Roof': 'سطح خاص',
  'Driver Room': 'غرفة سائق',
  'Maid Room': 'غرفة خادمة',
  'Storage': 'تخزين',
  'Security': 'حراسة',
  'Intercom': 'اتصال داخلي',
  'Central AC': 'تكييف مركزي',
  'Gym': 'صالة رياضية',
  'Playground': 'ملعب أطفال',
  'Smart Home': 'منزل ذكي',
  'Basement': 'قبو',
  'Concierge': 'خدمة استقبال',
  'Pet Friendly': 'مسموح بالحيوانات',
  'Guest House': 'بيت ضيافة',
  'Courtyard': 'فناء',
  // Common variations
  'Pool': 'مسبح',
  'AC': 'تكييف',
  'Air Conditioning': 'تكييف',
  'Central Air Conditioning': 'تكييف مركزي',
  'Covered Parking': 'موقف مغطى',
  'Private Garden': 'حديقة خاصة',
  'Private Pool': 'مسبح خاص',
  'Shared Pool': 'مسبح مشترك',
  'Furnished': 'مفروش',
  'Semi-Furnished': 'مفروش جزئياً',
  'Unfurnished': 'غير مفروش',
  'Kitchen': 'مطبخ',
  'Laundry': 'غسيل',
  'Laundry Room': 'غرفة غسيل',
  'Walk-in Closet': 'غرفة ملابس',
  'Study Room': 'غرفة دراسة',
  'Fireplace': 'مدفأة',
  'Sauna': 'ساونا',
  'Jacuzzi': 'جاكوزي',
  'BBQ Area': 'منطقة شواء',
  'Reception': 'استقبال',
  'Lobby': 'ردهة',
  'CCTV': 'كاميرات مراقبة',
  'Surveillance': 'كاميرات مراقبة',
  'Guard': 'حارس أمن',
  'Security System': 'نظام أمني',
  'Fire Alarm': 'إنذار حريق',
  'Backup Generator': 'مولد احتياطي',
  'Central Heating': 'تدفئة مركزية',
  'Double Glazing': 'زجاج مزدوج',
  'Soundproofing': 'عزل صوتي',
  'Wheelchair Access': 'وصول لذوي الاحتياجات',
  'Pets Allowed': 'مسموح بالحيوانات',
};

const utilityMap: Record<string, string> = {
  'Water': 'ماء',
  'Electricity': 'كهرباء',
  'Internet': 'إنترنت',
  'Gas': 'غاز',
  'Sewage': 'صرف صحي',
  'Trash Collection': 'جمع نفايات',
  'Phone': 'هاتف',
  'Fiber': 'ألياف ضوئية',
  'Flood Drainage': 'تصريف الفيضانات',
};

// Acronyms that should stay upper-cased when prettifying English keys.
const ACRONYMS: Record<string, string> = { ac: 'AC', cctv: 'CCTV', bbq: 'BBQ', tv: 'TV' };

/**
 * Canonical comparison form: lower-cased, with separators (_ -) collapsed to
 * single spaces. Lets DB keys like `covered_parking` match map keys like
 * `Covered Parking`.
 */
function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Turn a raw/snake_case key into a readable English label (e.g. `central_ac` → `Central AC`). */
function prettifyEnglish(value: string): string {
  return normalizeKey(value)
    .split(' ')
    .filter(Boolean)
    .map((word) => ACRONYMS[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function lookup(map: Record<string, string>, raw: string): string | undefined {
  if (map[raw]) return map[raw];
  const normalized = normalizeKey(raw);
  for (const [key, value] of Object.entries(map)) {
    if (normalizeKey(key) === normalized) return value;
  }
  return undefined;
}

/**
 * Translates an amenity to Arabic when a mapping exists. Handles snake_case DB
 * keys (`covered_parking`) as well as human labels (`Covered Parking`). When no
 * Arabic mapping is found, raw English/snake_case keys are prettified so the UI
 * never shows machine values like `central_ac`; strings that are already Arabic
 * are returned unchanged.
 */
export function translateAmenity(amenity: string, lang: string): string {
  if (lang === 'ar') {
    const translated = lookup(amenityMap, amenity);
    if (translated) return translated;
    // No Arabic mapping: prettify if it's a Latin/machine key, else assume Arabic.
    return /[a-z]/i.test(amenity) ? prettifyEnglish(amenity) : amenity;
  }
  // English: prettify raw/snake_case keys, leave already-nice labels untouched.
  if (/[_-]/.test(amenity) || amenity === amenity.toLowerCase()) {
    return prettifyEnglish(amenity);
  }
  return amenity;
}

/**
 * Translates a utility to Arabic when a mapping exists; otherwise prettifies
 * raw English keys (mirrors translateAmenity).
 */
export function translateUtility(utility: string, lang: string): string {
  if (lang === 'ar') {
    const translated = lookup(utilityMap, utility);
    if (translated) return translated;
    return /[a-z]/i.test(utility) ? prettifyEnglish(utility) : utility;
  }
  if (/[_-]/.test(utility) || utility === utility.toLowerCase()) {
    return prettifyEnglish(utility);
  }
  return utility;
}
